// Same API as react-places-autocomplete, backed by Places API (New). The older
// Places and Geocoding services are not available on our Google key.
import React, { useEffect, useRef, useState } from "react";

const BASE = "https://places.googleapis.com/v1";

const post = (path, body, fieldMask) =>
  fetch(`${BASE}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": process.env.REACT_APP_GOOGLE_API_KEY,
      ...(fieldMask ? { "X-Goog-FieldMask": fieldMask } : {}),
    },
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(`Places request failed: ${res.status}`);
    return res.json();
  });

export const geocodeByAddress = async (address) => {
  const { places = [] } = await post(
    "places:searchText",
    { textQuery: address },
    "places.formattedAddress,places.location,places.addressComponents"
  );
  if (!places.length) throw new Error("ZERO_RESULTS");
  return places.map((place) => ({
    formatted_address: place.formattedAddress,
    latLng: { lat: place.location.latitude, lng: place.location.longitude },
    address_components: (place.addressComponents || []).map((c) => ({
      long_name: c.longText,
      short_name: c.shortText,
      types: c.types,
    })),
  }));
};

export const getLatLng = async (result) => result.latLng;

export default function PlacesAutocomplete({ value, onChange, onSelect, children, debounce = 250 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const latest = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (input) => {
    clearTimeout(timer.current);
    const request = ++latest.current;
    if (!input) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      post("places:autocomplete", { input })
        .then(({ suggestions: found = [] }) => {
          if (request !== latest.current) return;
          setSuggestions(
            found
              .filter((s) => s.placePrediction)
              .map((s, index) => ({
                id: s.placePrediction.placeId,
                placeId: s.placePrediction.placeId,
                description: s.placePrediction.text.text,
                active: false,
                index,
              }))
          );
        })
        .catch(() => request === latest.current && setSuggestions([]))
        .finally(() => request === latest.current && setLoading(false));
    }, debounce);
  };

  const select = (suggestion) => {
    latest.current++;
    setSuggestions([]);
    setLoading(false);
    if (onSelect) onSelect(suggestion.description, suggestion.placeId);
    else if (onChange) onChange(suggestion.description);
  };

  const setActive = (index) =>
    setSuggestions((list) => list.map((s) => ({ ...s, active: s.index === index })));

  const getInputProps = (options = {}) => ({
    ...options,
    value,
    autoComplete: "off",
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": suggestions.length > 0,
    onChange: (event) => {
      onChange(event.target.value);
      search(event.target.value);
    },
    onBlur: (event) => {
      setSuggestions([]);
      if (options.onBlur) options.onBlur(event);
    },
    onKeyDown: (event) => {
      const current = suggestions.findIndex((s) => s.active);
      if (event.key === "Escape") setSuggestions([]);
      if (suggestions.length && event.key === "ArrowDown") {
        event.preventDefault();
        setActive((current + 1) % suggestions.length);
      }
      if (suggestions.length && event.key === "ArrowUp") {
        event.preventDefault();
        setActive((current - 1 + suggestions.length) % suggestions.length);
      }
      if (event.key === "Enter" && current >= 0) {
        event.preventDefault();
        select(suggestions[current]);
      }
      if (options.onKeyDown) options.onKeyDown(event);
    },
  });

  const getSuggestionItemProps = (suggestion, options = {}) => ({
    ...options,
    key: suggestion.id,
    role: "option",
    // mousedown, not click: the input's blur would clear the list first.
    onMouseDown: (event) => {
      event.preventDefault();
      select(suggestion);
    },
    onMouseEnter: () => setActive(suggestion.index),
  });

  return <>{children({ getInputProps, getSuggestionItemProps, suggestions, loading })}</>;
}
